import React from 'react';
import { motion } from 'framer-motion';
import {
  Users,
  Clock,
  UserX,
  Briefcase,
  UserCheck,
  Rocket,
  ArrowRight,
  ShieldCheck,
} from 'lucide-react';
import { useRedirect } from '../../Shared/Hooks/useRedirect ';

type AccentKey = 'sky' | 'amber' | 'rose';

const ACCENTS: Record<AccentKey, string> = {
  sky: 'border-sky-500/30 bg-sky-500/10 text-sky-400',
  amber: 'border-amber-500/30 bg-amber-500/10 text-amber-400',
  rose: 'border-rose-500/30 bg-rose-500/10 text-rose-400',
};

interface NavCardProps {
  label: string;
  description: string;
  icon: React.ReactNode;
  accent: AccentKey;
  delay: number;
  onClick: () => void;
}

const NavCard = ({ label, description, icon, accent, delay, onClick }: NavCardProps) => (
  <motion.button
    type="button"
    onClick={onClick}
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    whileHover={{ y: -3 }}
    whileTap={{ scale: 0.98 }}
    transition={{ duration: 0.35, delay }}
    className="group flex w-full items-center justify-between gap-3 rounded-2xl border border-slate-800 bg-slate-900/60 p-4 text-left transition hover:border-cyan-500/40 hover:bg-slate-900"
  >
    <div className="flex items-center gap-3">
      <span className={`flex h-10 w-10 items-center justify-center rounded-xl border ${ACCENTS[accent]}`}>
        {icon}
      </span>
      <div>
        <p className="text-sm font-bold text-slate-100">{label}</p>
        <p className="text-[11px] font-semibold text-slate-500">{description}</p>
      </div>
    </div>
    <ArrowRight className="h-4 w-4 flex-shrink-0 text-slate-600 transition group-hover:translate-x-0.5 group-hover:text-cyan-400" />
  </motion.button>
);

const GroupHeading = ({
  icon,
  title,
  subtitle,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
}) => (
  <div className="mb-4 flex items-center gap-3">
    <span className="flex h-9 w-9 items-center justify-center rounded-xl border border-slate-800 bg-slate-900 text-cyan-400">
      {icon}
    </span>
    <div>
      <h2 className="text-sm font-black uppercase tracking-widest text-slate-300">{title}</h2>
      <p className="text-[11px] font-semibold text-slate-500">{subtitle}</p>
    </div>
  </div>
);

function UserManagementComponent() {
  const redirect = useRedirect();

  return (
    <div className="min-h-screen bg-slate-950 p-4 text-slate-100 md:p-8">
      {/* Header */}
      <div className="mb-8 flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
          <ShieldCheck size={22} />
        </span>
        <div>
          <h1 className="text-xl font-black tracking-tight text-slate-100 md:text-2xl">User Management</h1>
          <p className="text-xs font-semibold text-slate-400 md:text-sm">
            Review and manage users, recruiters and early access accounts
          </p>
        </div>
      </div>

      <div className="space-y-8">
        {/* Candidates */}
        <section>
          <GroupHeading
            icon={<Users size={17} />}
            title="Candidates"
            subtitle="Candidate accounts across statuses"
          />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <NavCard
              label="All Users"
              description="Total users in the system"
              icon={<Users size={18} />}
              accent="sky"
              delay={0.1}
              onClick={() => redirect('/admin/all-users/candidate')}
            />
            <NavCard
              label="Under Review Users"
              description="Users who are under review"
              icon={<Clock size={18} />}
              accent="amber"
              delay={0.15}
              onClick={() => redirect('/admin/users/under_review/candidate')}
            />
            <NavCard
              label="Rejected Users"
              description="Users who were rejected"
              icon={<UserX size={18} />}
              accent="rose"
              delay={0.2}
              onClick={() => redirect('/admin/users/rejected/candidate')}
            />
          </div>
        </section>

        {/* Recruiters */}
        <section>
          <GroupHeading
            icon={<Briefcase size={17} />}
            title="Recruiters"
            subtitle="Recruiter accounts across statuses"
          />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <NavCard
              label="All Recruiters"
              description="Recruiters in the system"
              icon={<Briefcase size={18} />}
              accent="sky"
              delay={0.1}
              onClick={() => redirect('/admin/all-users/recruiter')}
            />
            <NavCard
              label="Under Review Recruiters"
              description="Recruiters who are under review"
              icon={<Clock size={18} />}
              accent="amber"
              delay={0.15}
              onClick={() => redirect('/admin/users/under_review/recruiter')}
            />
            <NavCard
              label="Rejected Recruiters"
              description="Recruiters who were rejected"
              icon={<UserX size={18} />}
              accent="rose"
              delay={0.2}
              onClick={() => redirect('/admin/users/rejected/recruiter')}
            />
          </div>
        </section>

        {/* Early Access */}
        <section>
          <GroupHeading
            icon={<Rocket size={17} />}
            title="Early Access"
            subtitle="Pre-launch accounts and signups"
          />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <NavCard
              label="All Early Access Users"
              description="Early access users in the system"
              icon={<Users size={18} />}
              accent="sky"
              delay={0.1}
              onClick={() => redirect('/admin/all/early-acces/users/')}
            />
            <NavCard
              label="All Early Access Recruiters"
              description="Early access recruiters"
              icon={<Briefcase size={18} />}
              accent="amber"
              delay={0.15}
              onClick={() => redirect('/admin/early-acces/users/recruiter')}
            />
            <NavCard
              label="All Early Access Candidate"
              description="Early access candidates"
              icon={<UserCheck size={18} />}
              accent="rose"
              delay={0.2}
              onClick={() => redirect('/admin/early-acces/users/candidate')}
            />
          </div>
        </section>
      </div>
    </div>
  );
}

export default UserManagementComponent;
